import { FC } from 'react'
import { useTranslation } from 'react-i18next'
import {
  AssetSize,
  Breadcrumb,
  BreadcrumbItem,
  BreadcrumbSeparator,
  Button,
  ButtonFunction,
  ButtonSize,
  Chip,
  ChipSize,
  ChipVariant,
  Divider,
  DividerLine,
  DividerOrientation,
  Heading,
  HeadingLevel,
  HeadingTag,
  Icon,
  IconButton,
  IconButtonFunction,
  IconButtonSize,
  IconName,
  Link,
  LinkFunction,
  LinkSize,
  LinkTarget,
  LinkVariant,
  Tooltip,
  TooltipPlacement
} from '@enbw/ui-react'
import { NavLink } from 'react-router'
import styles from './styles.module.scss'
import { BILLING_URI, EV_BACKEND_URI, KBP_URI, MEK_URI } from '@/constants'
import { ContractHeader, ContractType } from '@/domain/contract/types/contract'
import { ContractStatusLabel } from '@/components/ContractStatusLabel'
import { useBackToContractsSearch } from '@/hooks/useBackToContractsSearch'
import { FlyoutId } from '@/domain/contract/layouts/ContractDetailsLayout/flyoutId'
import { Paths } from '@/paths'

const KBP_TENANT_ID = '100'

type ContractHeaderProps = {
  value: ContractHeader
  openFlyoutIds: FlyoutId[]
  onFlyoutToggle: (id: FlyoutId) => void
  messageCount: number
}

export const ContractDetailsHeader: FC<ContractHeaderProps> = ({ value, openFlyoutIds, onFlyoutToggle, messageCount }) => {
  const { t } = useTranslation()
  const { navigateBackToSearch, backToSearchUrl } = useBackToContractsSearch()

  const openSearchInNewTab = (): void => {
    window.open(backToSearchUrl, '_blank', 'noreferrer')
  }

  const handleBackButtonClick = (event: React.MouseEvent): void => {
    if (event.ctrlKey || event.metaKey) {
      openSearchInNewTab()
    } else {
      navigateBackToSearch()
    }
  }

  const handleBackButtonAuxClick = (event: React.MouseEvent): void => {
    if (event.button === 1) {
      event.preventDefault()
      openSearchInNewTab()
    }
  }

  const getContractTypeLabel = (type: ContractType): string => {
    switch (type) {
      case ContractType.B2C:
        return t('contract.header.values.contractType.b2c')
      case ContractType.B2B:
        return t('contract.header.values.contractType.b2b')
      case ContractType.UNSPECIFIED:
        return t('contract.header.values.contractType.unspecified')
    }
  }

  const { contractNumber, contractType, contractStatus, billingAccountId, businessPartnerIdExternal, myEnergyKeySubject, legacyEndUserId } = value

  const evBackendUrl = `${EV_BACKEND_URI}/enduser/${legacyEndUserId}`
  const billingUrl = billingAccountId ? `${BILLING_URI}/support/overview/${billingAccountId}` : undefined
  const kbpUrl = businessPartnerIdExternal ? `${KBP_URI}/${KBP_TENANT_ID}/${businessPartnerIdExternal}` : undefined
  const mekUrl = myEnergyKeySubject ? `${MEK_URI}/auth0-delegated-admin/users/${myEnergyKeySubject}` : undefined

  const isCustomerFlyoutOpen = openFlyoutIds.includes(FlyoutId.Customer)
  const isMessagesFlyoutOpen = openFlyoutIds.includes(FlyoutId.Messages)

  const showMessagesButton = messageCount > 0
  const showCustomerButton = contractType === ContractType.B2C
  const showFlyoutButtons = showMessagesButton || showCustomerButton

  return (
    <div className={styles.contractHeader}>
      <Breadcrumb>
        <BreadcrumbItem>
          <NavLink to={Paths.CONTRACTS_SEARCH} slot="label">
            <span>{t('contracts.title')}</span>
          </NavLink>
        </BreadcrumbItem>
        <BreadcrumbSeparator />
        <BreadcrumbItem current>
          <span slot="label">{contractNumber}</span>
        </BreadcrumbItem>
      </Breadcrumb>

      <div className={styles.headerRow}>
        <div className={styles.headerLeft}>
          <div className={styles.headerTitle}>
            <IconButton
              className={styles.backButton}
              label={t('common.backToSearch')}
              name={IconName.ArrowUp}
              onClick={handleBackButtonClick}
              onAuxClick={handleBackButtonAuxClick}
              function={IconButtonFunction.Tertiary}
              size={IconButtonSize.Small}
            />
            <Heading level={HeadingLevel.H1} tag={HeadingTag.H1}>
              {contractNumber}
            </Heading>
          </div>

          <div className={styles.headerTags}>
            <Chip label={getContractTypeLabel(contractType)} variant={ChipVariant.FeedbackInfo} />
            <ContractStatusLabel status={contractStatus} size={ChipSize.Default} />
          </div>
        </div>

        <div className={styles.headerLinks}>
          <Tooltip placement={TooltipPlacement.Bottom}>
            <Link
              slot="trigger"
              label={t('contract.header.evBackend.title')}
              href={evBackendUrl}
              function={LinkFunction.Secondary}
              variant={LinkVariant.Title}
              size={LinkSize.Small}
              target={LinkTarget.Blank}
            >
              <Icon slot="trailing-icon" name={IconName.OpenInNew} size={AssetSize.XSmall} />
            </Link>
            <span slot="content">{t('contract.header.evBackend.description')}</span>
          </Tooltip>

          <Tooltip placement={TooltipPlacement.Bottom}>
            <Link
              slot="trigger"
              label={t('contract.header.billing.title')}
              href={billingUrl}
              function={LinkFunction.Secondary}
              variant={LinkVariant.Title}
              size={LinkSize.Small}
              target={LinkTarget.Blank}
              disabled={!billingUrl}
            >
              <Icon slot="trailing-icon" name={IconName.OpenInNew} size={AssetSize.XSmall} />
            </Link>
            <span slot="content">{billingUrl ? t('contract.header.billing.description') : t('contract.header.billing.notFound')}</span>
          </Tooltip>

          <Tooltip placement={TooltipPlacement.Bottom}>
            <Link
              slot="trigger"
              label={t('contract.header.kbp.title')}
              href={kbpUrl}
              function={LinkFunction.Secondary}
              variant={LinkVariant.Title}
              size={LinkSize.Small}
              target={LinkTarget.Blank}
              disabled={!kbpUrl}
            >
              <Icon slot="trailing-icon" name={IconName.OpenInNew} size={AssetSize.XSmall} />
            </Link>
            <span slot="content">{kbpUrl ? t('contract.header.kbp.description') : t('contract.header.kbp.notFound')}</span>
          </Tooltip>

          <Tooltip placement={TooltipPlacement.Bottom}>
            <Link
              slot="trigger"
              label={t('contract.header.mek.title')}
              href={mekUrl}
              function={LinkFunction.Secondary}
              variant={LinkVariant.Title}
              size={LinkSize.Small}
              target={LinkTarget.Blank}
              disabled={!mekUrl}
            >
              <Icon slot="trailing-icon" name={IconName.OpenInNew} size={AssetSize.XSmall} />
            </Link>
            <span slot="content">{t('contract.header.mek.description')}</span>
          </Tooltip>
        </div>
        {showFlyoutButtons && <Divider className={styles.verticalDivider} orientation={DividerOrientation.Vertical} line={DividerLine.Subtle} />}
        {showMessagesButton && (
          <Button
            className={isMessagesFlyoutOpen ? styles.flyoutButtonActive : undefined}
            function={isMessagesFlyoutOpen ? ButtonFunction.Primary : ButtonFunction.Secondary}
            onClick={() => onFlyoutToggle(FlyoutId.Messages)}
            size={ButtonSize.Small}
          >
            <div slot="label" className={styles.messagesFlyoutButtonLabel}>
              {t('contract.header.messagesFlyout.toggleLabel', { messageCount: messageCount })}
            </div>
            <Icon slot="leading-icon" name={IconName.Attention} size={AssetSize.XSmall} />
          </Button>
        )}
        {showCustomerButton && (
          <Button
            className={isCustomerFlyoutOpen ? styles.flyoutButtonActive : undefined}
            function={isCustomerFlyoutOpen ? ButtonFunction.Primary : ButtonFunction.Secondary}
            label={t('contract.header.customerFlyout.toggleLabel')}
            onClick={() => onFlyoutToggle(FlyoutId.Customer)}
            size={ButtonSize.Small}
          >
            <Icon slot="leading-icon" name={IconName.User} size={AssetSize.XSmall} />
          </Button>
        )}
      </div>

      <Divider orientation={DividerOrientation.Horizontal} line={DividerLine.Subtle} />
    </div>
  )
}
